import { musicPlatform } from 'fitbeat.gym-core/config';
import React, { FunctionComponent } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { connect } from 'react-redux';
import { Button, ButtonTypes } from '../../../components';
import Theme from '../../../theme';
import { defaultScale } from '../../../utils/scaling';
import { AppStateFocusedEvents } from '../../../ui';
import { closeSetupScreen } from '../../actions';
import { IAppStore } from '../../reducer';
import { orientationSelector } from '../../selectors';
import { DeviceSetupSubScreenHeader } from '../DeviceSetup/DeviceSetupSubScreenHeader';
import {
  backFromPlaylistSelectScreenPressed,
  createNewPlaylistClicked,
  onError,
  playlistsSelectScreenFocused,
} from './actions';
import { IPlaylist } from './interfaces';
import { PlaylistAlbumTile } from './PlaylistAlbumTile';
import {
  getPlaylistLoadingStateSelector, getPlaylistsSelector,
  getSelectedMusicPlayerSelector,
} from './selectors';
import { openLink } from '../../../utils/linkUtils';

interface IProps {
  playlists: IPlaylist[];
  fbPlaylists: IPlaylist[];
  userPlaylists: IPlaylist[];
  recommendedPlaylists: IPlaylist[];
  t: (key: string, options?: any) => string;
  playlistsLoading: boolean;
  onError: (e: Error, caller: string) => void;
  playlistsSelectScreenFocused: () => void;
  createNewPlaylistClicked: () => void;
  orientation: string;
  musicPlayer: musicPlatform;
  backPressed: () => void;
}

const PlaylistSelectScreenComponent: FunctionComponent<IProps> = ({t, ...props}) => {

  const openMusicApp = async () => {
    try {
      if (props.musicPlayer === musicPlatform.Spotify) {
        await openLink('spotify://');
      } else if (props.musicPlayer === musicPlatform.AppleMusic) {
        await openLink('music://');
      }
    } catch (e) {
      props.onError(e, 'create new playlist clicked');
    }
  };

  const handleScreenFocus = () => {
    props.playlistsSelectScreenFocused();
  };

  const portrait = (): boolean => {
    return props.orientation === 'PORTRAIT';
  };

  return (
    <View style={{ flex: 1 }}>
      <AppStateFocusedEvents
        onScreenFocus={handleScreenFocus}
      />
      <DeviceSetupSubScreenHeader
        title={t('music')}
        onBackPress={props.backPressed}
      />
      {
        !portrait() &&
        <Text style={[styles.header, { marginVertical: defaultScale(20) }]}>
          Select {props.musicPlayer} Playlist
        </Text>
      }
      {
        props.playlistsLoading &&
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator
            size='large'
            animating={true}
            color={Theme.PRIMARY}
          />
        </View>
      }
      {
        !props.playlistsLoading &&
        <ScrollView contentContainerStyle={styles.playlistAlbumContainer} horizontal={!portrait()}>
          {
            portrait() &&
            <View style={{ width: '100%', alignItems: 'center' }}>
              <Text style={[styles.header, { marginVertical: defaultScale(30) }]}>
                {t('selectPlaylist', { musicPlayer: props.musicPlayer })}
              </Text>
            </View>
          }
          {
            props.fbPlaylists.map((item) => (
              <PlaylistAlbumTile playlistItem={item} key={item.id} />))
          }
          {
            props.userPlaylists.map((item) => (
              <PlaylistAlbumTile playlistItem={item} userPlaylist={true} key={item.id} />))
          }
          {
            props.recommendedPlaylists.map((item) => (
              <PlaylistAlbumTile playlistItem={item} key={item.id} />))
          }
        </ScrollView>
      }
      <Button
        title={t('setupNewPlaylist', { musicPlayer: props.musicPlayer })}
        fontColor={Theme.PRIMARY}
        buttonType={ButtonTypes.TRANSPARENT}
        onPress={openMusicApp}
      />
    </View>
  );
};

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  return {
    playlists: getPlaylistsSelector(state),
    fbPlaylists: state.music.fbPlaylists,
    userPlaylists: state.music.userPlaylists,
    recommendedPlaylists: state.music.recommendedPlaylists,
    playlistsLoading: getPlaylistLoadingStateSelector(state),
    orientation: orientationSelector(state),
    musicPlayer: getSelectedMusicPlayerSelector(state),
  };
};

const mapDispatchToProps = (dispatch: any): Partial<IProps> => {
  return {
    closeSetupScreen: () => dispatch(closeSetupScreen()),
    onError: (e, caller) => dispatch(onError(e, caller)),
    playlistsSelectScreenFocused: () => dispatch(playlistsSelectScreenFocused()),
    createNewPlaylistClicked: () => dispatch(createNewPlaylistClicked()),
    backPressed: () => dispatch(backFromPlaylistSelectScreenPressed()),
  };
};

export const PlaylistSelectScreen = connect(mapStateToProps, mapDispatchToProps)(PlaylistSelectScreenComponent);

const styles = StyleSheet.create({
  header: {
    ...Theme.textStyle.bodyCopyMed,
    color: Theme.WHITE,
    alignItems: 'center',
  },
  playlistAlbumContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
});
